import {
  type SupplierListItem,
  type VendorCatalogProduct,
} from '@choco-admin/types';
import { Button } from '@heroui/react';
import React, { useState } from 'react';

import { AlertCard } from '../../../../../../../components/AlertCard';
import { SearchInput } from '../../../../../../../components/FormComponents';
import { useDebounce } from '../../../../../../../hooks';
import { useVendorCatalogBrowse } from '../hooks/useVendorCatalogBrowse';

import { CatalogProductDetailModal } from './CatalogProductDetailModal';
import { CatalogProductTable } from './CatalogProductTable';
import { CustomCatalogPicker } from './CustomCatalogPicker';

interface CatalogProductBrowserProps {
  supplier: SupplierListItem;
}

const SEARCH_DEBOUNCE_MS = 300;
// Non-empty sentinel: an empty key renders no selection at all. Mapped to
// `undefined` (no filter) before it reaches the browse hook.
const ALL_CATALOGS = '__all__';

const CATALOG_FILTER_OPTIONS = [{ key: ALL_CATALOGS, label: 'All catalogs' }];

export const CatalogProductBrowser: React.FC<CatalogProductBrowserProps> = ({
  supplier,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [subCatalogId, setSubCatalogId] = useState(ALL_CATALOGS);
  const [selectedProduct, setSelectedProduct] =
    useState<VendorCatalogProduct | null>(null);
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const {
    products,
    totalCount,
    isLoading,
    isLoadingMore,
    errorMessage,
    hasNextPage,
    loadMore,
  } = useVendorCatalogBrowse({
    vendorId: supplier.id,
    searchTerm: debouncedSearch,
    subCatalogId: subCatalogId === ALL_CATALOGS ? undefined : subCatalogId,
  });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">Catalog products</h3>
        <p className="text-sm text-default-500">
          Read-only view of this distributor&apos;s catalog. Product content is
          managed in Retool.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search products by name or code..."
          ariaLabel="Search catalog products"
          className="md:max-w-md md:flex-1"
        />
        <CustomCatalogPicker
          vendorId={supplier.id}
          label="Filter by catalog"
          testId="catalog-browser-catalog-filter"
          fixedOptions={CATALOG_FILTER_OPTIONS}
          value={subCatalogId}
          onChange={setSubCatalogId}
        />
      </div>

      {errorMessage ? (
        <AlertCard
          type="error"
          title="Could not load catalog products"
          message={errorMessage}
        />
      ) : (
        <>
          {!isLoading && (
            <p className="text-sm text-default-500">
              {totalCount} {totalCount === 1 ? 'product' : 'products'}
              {subCatalogId !== ALL_CATALOGS || debouncedSearch.trim()
                ? ' matching filters'
                : ''}
            </p>
          )}

          <CatalogProductTable
            products={products}
            isLoading={isLoading}
            onSelectProduct={setSelectedProduct}
          />

          {hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="flat"
                color="default"
                onPress={loadMore}
                isLoading={isLoadingMore}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <CatalogProductDetailModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  );
};
